import type { Placement } from '@floating-ui/react';
import type { ReactNode } from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import { useState } from 'react';

import { cn } from '@poseidon/components/cn';

// Stable middleware identity — the factories take no per-instance args, so hoisting to module scope
// avoids re-allocating the array on every render (these badges re-render on every pan/zoom frame of
// the graph, so per-frame garbage here is worth avoiding).
const HOVER_MIDDLEWARE = [offset(6), flip({ padding: 8 }), shift({ padding: 8 })];

interface HoverPeoplePopoverProps {
  /** The always-visible hover target (the avatar pile or count chip). */
  trigger: ReactNode;
  /** The floating panel contents (header + rows / face grid). */
  children: ReactNode;
  /** Extra classes for the trigger wrapper (e.g. `flex items-center`). */
  triggerClassName?: string;
  /** Extra classes for the floating panel — pass a FIXED width here (e.g. `w-72`) so the
   * panel never resizes with its content. */
  panelClassName?: string;
  /** Testid for the floating panel (defaults to `hover-people-panel`). */
  panelTestId?: string;
  /** Where the panel opens; `flip()` still moves it when it won't fit. Defaults to `top-start`. */
  placement?: Placement;
}

/**
 * A hover-revealed floating panel anchored to a small trigger (an avatar pile / count chip).
 * Replaces the old hand-rolled `group-hover` + `absolute bottom-full` popovers, which had three
 * problems this fixes with Floating UI:
 *
 *  1. **Stable size + position** — the panel is portaled and positioned by Floating UI from a
 *     single `top-start` anchor. Give it a FIXED width via `panelClassName` so it stops resizing
 *     and jumping as different people (with different name lengths) show up.
 *  2. **Flips when it won't fit above** — `flip()` drops the panel below the trigger when there
 *     isn't enough room above (the old `bottom-full` could only ever open upward, off-screen).
 *  3. **Diagonal "safe triangle" hover** — `safePolygon()` lets the cursor move diagonally from the
 *     trigger straight to any row (the Amazon menu pattern) without the panel closing, instead of
 *     forcing a straight-up path through a narrow CSS bridge.
 *
 * Because the panel is portaled to the document body, it also escapes the pan/zoom graph
 * viewport's `overflow` clipping and transform, so it renders unclipped and unscaled.
 */
export function HoverPeoplePopover({
  trigger,
  children,
  triggerClassName,
  panelClassName,
  panelTestId = 'hover-people-panel',
  placement = 'top-start',
}: HoverPeoplePopoverProps) {
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    strategy: 'fixed',
    // Re-anchor every frame WHILE OPEN so the panel keeps tracking the badge even when the graph is
    // panned/zoomed via CSS transform (which scroll/resize observers don't emit). The rAF loop only
    // runs while the panel is mounted — i.e. while hovered — so the cost is bounded to that window.
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { animationFrame: true }),
    middleware: HOVER_MIDDLEWARE,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { handleClose: safePolygon({ buffer: 1 }) }),
  ]);

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()} className={cn('w-max', triggerClassName)}>
        {trigger}
      </div>
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            data-testid={panelTestId}
            style={floatingStyles}
            {...getFloatingProps()}
            // The panel is portaled, but React events still bubble through the portal to whatever the
            // trigger is nested under (e.g. a graph node card's onClick → open the step editor). Contain
            // clicks at the panel root so clicking the header / padding / a chip never leaks to the card.
            // `font-sans`: <body> does not carry the app font (the page root sets it), so a panel
            // portaled there would fall back to the body's.
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'z-50 max-h-[70vh] overflow-y-auto rounded-xl font-sans border border-lynch-200 bg-white p-2 shadow-lg dark:border-slate-700 dark:bg-slate-900',
              panelClassName,
            )}>
            {children}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
